import { commonTranslations } from "./common";
import { itemTranslations } from "./item";
import { itemPriceTranslations } from "./itemPrice";
import { salesOrderTranslations } from "./salesOrder";
import { salesOrderItemTranslations } from "./salesOrderItem";
import { pricingRuleTranslations } from "./pricingRule";
// Import other DocType translations as needed
import type { Language } from "../types";

/**
 * Scalable doctype translation map.
 * Add new doctypes here as they are implemented.
 */
const doctypeTranslations: Record<string, Record<string, { en: string; th: string }>> = {
  Item: itemTranslations,
  "Item Price": itemPriceTranslations,
  "Sales Order": salesOrderTranslations,
  "Sales Order Item": salesOrderItemTranslations,
  "Pricing Rule": pricingRuleTranslations,
  // Add more doctypes as needed
};

/**
 * Get meta translation for a field.
 * Priority: DocType-specific > Common
 * 
 * @param doctype - The DocType name (e.g., "Item", "Sales Order")
 * @param fieldname - The field name (e.g., "item_code")
 * @param language - The language code ("en" | "th")
 * @returns The translated label or null if not found
 */
export function getMetaTranslation(
  doctype: string,
  fieldname: string,
  language: Language
): string | null {
  // Try doctype-specific first
  const doctypeKey = `${doctype}:${fieldname}`;
  const dt = doctypeTranslations[doctype];
  if (dt?.[doctypeKey]) {
    return dt[doctypeKey][language] || null;
  }
  
  // Fallback to common
  const commonKey = `common:${fieldname}`;
  if (commonTranslations[commonKey]) {
    return commonTranslations[commonKey][language] || null;
  }
  
  // Development mode: Log lookup attempts for debugging
  if (process.env.NODE_ENV === "development" && language !== "en") {
    const debugKey = `__translation_lookup_${doctype}_${fieldname}_${language}`;
    if (!(globalThis as any)[debugKey]) {
      (globalThis as any)[debugKey] = true;
      console.debug(
        `[getMetaTranslation] Looked up: ${doctypeKey} and ${commonKey}, not found`
      );
    }
  }
  
  return null;
}

/**
 * Get UI label translation (for non-field labels like "Total Items", "Average Price", etc.)
 * 
 * @param key - The UI label key (e.g., "ui_total_items", "ui_subtotal")
 * @param language - The language code ("en" | "th")
 * @returns The translated label or null if not found
 */
export function getUITranslation(
  key: string,
  language: Language
): string | null {
  const uiKey = `common:ui_${key}`;
  if (commonTranslations[uiKey]) {
    return commonTranslations[uiKey][language] || null;
  }
  return null;
}

/**
 * Get status value translation (for dynamic status values like "Draft", "Submitted", "To Deliver", etc.)
 * 
 * @param statusValue - The status value (e.g., "Draft", "To Deliver", "Not Delivered")
 * @param language - The language code ("en" | "th")
 * @returns The translated status or the original value if not found
 */
export function getStatusTranslation(
  statusValue: string | null | undefined,
  language: Language
): string {
  if (!statusValue) return statusValue || "";
  
  // Normalize the status value (handle case variations and spaces)
  const normalized = statusValue.trim().toLowerCase().replace(/\s+/g, "_");
  const statusKey = `common:status_${normalized}`;
  
  if (commonTranslations[statusKey]) {
    return commonTranslations[statusKey][language] || statusValue;
  }
  
  // Return original if no translation found
  return statusValue;
}

/**
 * Get section title translation (for section labels like "Status Summary", "Items", "Totals", etc.)
 * 
 * @param sectionLabel - The section label (e.g., "Status Summary", "Items", "Additional Discount and Payment")
 * @param language - The language code ("en" | "th")
 * @returns The translated section label or the original value if not found
 */
export function getSectionTitleTranslation(
  sectionLabel: string | null | undefined,
  language: Language
): string {
  if (!sectionLabel) return sectionLabel || "";
  
  // Normalize the section label (handle case variations, spaces, and "&")
  // First, normalize "&" to "and" for consistency
  let normalized = sectionLabel.trim().toLowerCase();
  // Replace "&" with "and" (handle both "&" and "and")
  normalized = normalized.replace(/\s*&\s*/g, "_and_");
  // Replace remaining spaces with underscores
  normalized = normalized.replace(/\s+/g, "_");
  // Clean up multiple underscores
  normalized = normalized.replace(/_+/g, "_");
  // Remove leading/trailing underscores
  normalized = normalized.replace(/^_+|_+$/g, "");
  
  const sectionKey = `common:section_${normalized}`;
  
  if (commonTranslations[sectionKey]) {
    return commonTranslations[sectionKey][language] || sectionLabel;
  }
  
  // Try alternative normalization (without "and" replacement) for backward compatibility
  const altNormalized = sectionLabel.trim().toLowerCase().replace(/\s+/g, "_");
  const altSectionKey = `common:section_${altNormalized}`;
  
  if (commonTranslations[altSectionKey]) {
    return commonTranslations[altSectionKey][language] || sectionLabel;
  }
  
  // Return original if no translation found
  return sectionLabel;
}

/**
 * Get tab label translation (for tab labels like "Details", "Address & Contact", "Terms", etc.)
 * 
 * @param tabLabel - The tab label (e.g., "Details", "Address & Contact", "Terms")
 * @param language - The language code ("en" | "th")
 * @returns The translated tab label or the original value if not found
 */
export function getTabLabelTranslation(
  tabLabel: string | null | undefined,
  language: Language
): string {
  if (!tabLabel) return tabLabel || "";
  
  // Normalize the tab label (handle case variations, spaces, and "&")
  // First, normalize "&" to "and" for consistency
  let normalized = tabLabel.trim().toLowerCase();
  // Replace "&" with "and" (handle both "&" and "and")
  normalized = normalized.replace(/\s*&\s*/g, "_and_");
  // Replace remaining spaces with underscores
  normalized = normalized.replace(/\s+/g, "_");
  // Clean up multiple underscores
  normalized = normalized.replace(/_+/g, "_");
  // Remove leading/trailing underscores
  normalized = normalized.replace(/^_+|_+$/g, "");
  
  const tabKey = `common:tab_${normalized}`;
  
  if (commonTranslations[tabKey]) {
    return commonTranslations[tabKey][language] || tabLabel;
  }
  
  // Try alternative normalization (without "and" replacement) for backward compatibility
  const altNormalized = tabLabel.trim().toLowerCase().replace(/\s+/g, "_");
  const altTabKey = `common:tab_${altNormalized}`;
  
  if (commonTranslations[altTabKey]) {
    return commonTranslations[altTabKey][language] || tabLabel;
  }
  
  // Return original if no translation found
  return tabLabel;
}

/**
 * Get doctype label translation (for doctype names like "BOM", "Product Bundle", "Item Alternative", etc.)
 * 
 * @param doctypeName - The doctype name (e.g., "BOM", "Product Bundle", "Item Alternative")
 * @param language - The language code ("en" | "th")
 * @returns The translated doctype name or the original value if not found
 */
export function getDoctypeLabelTranslation(
  doctypeName: string | null | undefined,
  language: Language
): string {
  if (!doctypeName) return doctypeName || "";
  
  // Normalize the doctype name (handle case variations and spaces)
  let normalized = doctypeName.trim().toLowerCase();
  // Replace spaces with underscores
  normalized = normalized.replace(/\s+/g, "_");
  // Clean up multiple underscores
  normalized = normalized.replace(/_+/g, "_");
  // Remove leading/trailing underscores
  normalized = normalized.replace(/^_+|_+$/g, "");
  
  const doctypeKey = `common:doctype_${normalized}`;
  
  if (commonTranslations[doctypeKey]) {
    return commonTranslations[doctypeKey][language] || doctypeName;
  }
  
  // Return original if no translation found
  return doctypeName;
}

